<script>
import {mapActions, mapGetters, mapState} from "vuex";
import {checkIconName} from "@/mixins/checkIconName";
import {useMobileMenu} from "@/mixins/useMobileMenu";
import ServiceColumn from "@/components/navigation/private/ServiceColumn.vue";
import SubServiceColumn from "@/components/navigation/private/SubServiceColumn.vue";
import MobileCommunePartnerDisplay from "@/components/navigation/private/MobileCommunePartnerDisplay.vue";
import loginNew from "loginNew";

export default {
  name: "ReworkedNavigationDrawer",
  components: {MobileCommunePartnerDisplay, SubServiceColumn, ServiceColumn},
  mixins: [checkIconName, useMobileMenu],
  computed: {
    ...mapState("user", ["hideKommunalportal"]),
    ...mapGetters("user", ["userCommuneName", "isBildungsportalUser", "showBildungsportal", "isNewEmployee"]),
    ...mapGetters("navigation", ["serviceAreas", "getServices", "getSubServices", "getSelectedServiceAreaId"]),
    filteredItems() {
      let items = [];

      for (const serviceArea of this.serviceAreas) {
        if (serviceArea.title !== "Bildungsportal" || this.isBildungsportalUser) {
          items.push(serviceArea);
        }
      }

      if(this.showBildungsportal) {
        const bildungsportalServiceAreas = ["Bildungsportal", "Sponsoring", "Neuigkeiten"]
        items = items.filter(item => bildungsportalServiceAreas.includes(item.title));
      }

      return items;
    },
    showServiceAreaColumn() {
      return !this.useMobileMenu || (!this.getServices && !this.getSubServices);
    },
    showServiceColumn() {
      return this.useMobileMenu && this.getServices && !this.getSubServices;
    },
    showSubServicesColumn() {
      return this.useMobileMenu && this.getSubServices;
    },
    activeServiceAreaId() {
      if (this.getSelectedServiceAreaId) {
        return this.getSelectedServiceAreaId;
      }

      const routeMatchedServiceArea = this.filteredItems.find(serviceArea => serviceArea.link && this.isRouteActive(serviceArea.link));

      if (routeMatchedServiceArea) {
        return routeMatchedServiceArea.id;
      }

      return null;
    },
    communeName() {
      if (!this.isNewEmployee) {
        return this.userCommuneName;
      }

      return "Kommunen"
    },
    showExitBildungsportalButton() {
      return !this.hideKommunalportal && this.showBildungsportal;
    }
  },
  data: () => ({
    selectedServiceArea: null,
    searchQuery: "",
  }),
  methods: {
    ...mapActions("navigation", ["setSelectedServiceAreaId"]),
    ...mapActions("user", ["setShowBildungsportal"]),
    isRouteActive(link) {
      if (!link || typeof link !== "string") {
        return false;
      }

      const normalizedLink = link.endsWith("/") ? link.slice(0, -1) : link;
      const currentPath = this.$route?.path || "";
      return currentPath === normalizedLink || currentPath.startsWith(`${normalizedLink}/`);
    },
    exitBildungsportal() {
      this.setShowBildungsportal(false);
    },
    isServiceAreaActive(serviceArea) {
      return this.activeServiceAreaId === serviceArea.id;
    },
    onServiceAreaClick(serviceArea) {
      if(serviceArea.link) {
        this.setSelectedServiceAreaId(null);
        return;
      }
      console.log(`Service area ${serviceArea.title} clicked`);
      this.setSelectedServiceAreaId(serviceArea.id);
    },
    onSearchSubmit() {
      this.setSelectedServiceAreaId(null)
      this.$router.push(`/search?q=${encodeURIComponent(this.searchQuery)}`);
    },
    onLogout() {
      loginNew.logout();
    }
  }
}
</script>

<template>
<div class="hide-scrollbar">
  <template v-if="showServiceAreaColumn">
  <div style="padding: 24px 24px; padding-right: 0; padding-left: 20px;">
    <template v-if="!useMobileMenu">
    <div>
    <router-link :to="{ name: 'home' }">
      <img src="@/assets/logo/NEW-KoPo-Logo.svg" />
    </router-link>
  </div>
  <v-text-field style="padding-right: 24px" v-model="searchQuery" solo single-line dense class="search-text-field" placeholder="Suchen" filled prepend-inner-icon="mdi-magnify" dark @keydown.enter="onSearchSubmit" />
    </template>

    <template v-if="isNewEmployee">
    <p class="menu-subtitle" style="margin-bottom: 4px">NEW FÜR MITARBEITER</p>
    <v-list style="margin-bottom: 24px">
      <v-list-item style="padding: 0; padding-left: 8px" class="menu-item service-area-item" @click="onServiceAreaClick({ id: 'management-dashboard'})">
        <v-list-item-content class="item-text" :class="getSelectedServiceAreaId === 'management-dashboard' ? 'item-text-active' : 'item-text-inactive'">
          <div class="d-flex align-center menu-item-row">
          <v-icon :class="getSelectedServiceAreaId === 'management-dashboard' ? 'menu-icon-active' : 'menu-icon-inactive'" left>$menuDashboard</v-icon>
          Management Dashboard
          <div style="flex-grow: 1"></div>
          <span v-if="getSelectedServiceAreaId === 'management-dashboard'" class="menu-chevron" aria-hidden="true"></span>
          </div>
          </v-list-item-content>
      </v-list-item>
    </v-list>
  </template>
  <p class="menu-subtitle" style="margin-bottom: 4px">NEW FÜR {{ communeName }}</p>
  <v-list>
    <v-list-item style="padding: 0; padding-left: 8px" class="menu-item service-area-item" v-for="serviceArea in filteredItems" :key="serviceArea.id" :to="serviceArea.link" @click="onServiceAreaClick(serviceArea)">
      <v-list-item-content class="item-text" :class="isServiceAreaActive(serviceArea) ? 'item-text-active' : 'item-text-inactive'">
        <div class="d-flex align-center menu-item-row">
          <v-icon :class="isServiceAreaActive(serviceArea) ? 'menu-icon-active logo-white' : 'menu-icon-inactive'" left>{{ checkIconName(serviceArea) }}</v-icon>
          {{ serviceArea.title }}
          <div style="flex-grow: 1"></div>
          <span v-if="isServiceAreaActive(serviceArea)" class="menu-chevron" aria-hidden="true"></span>
        </div>
      </v-list-item-content>
    </v-list-item>
    <v-list-item style="padding: 0; padding-left: 8px" class="menu-item service-area-item" @click="exitBildungsportal" v-if="showExitBildungsportalButton">
      <v-list-item-content class="item-text item-text-inactive">
        <div class="d-flex align-center menu-item-row">
          <v-icon>$menuKommunalportal</v-icon>
          NEW Kommunalportal
        </div>
      </v-list-item-content>
    </v-list-item>
  </v-list>
    <mobile-commune-partner-display v-if="useMobileMenu" />
  </div>
  </template>
  <template v-else-if="showServiceColumn">
    <service-column />
  </template>
  <template v-else-if="showSubServicesColumn">
    <sub-service-column />
  </template>
  <v-list style="padding: 12px 24px">
    <v-list-item style="padding: 0" class="menu-item" href="https://login.new.de/settings" target="_blank">
      <v-list-item-content class="bottom-menu item-text">
        <div class="d-flex align-center">
          <v-icon left>$menuAccount</v-icon>
          Account
        </div>
      </v-list-item-content>
    </v-list-item>
    <v-list-item style="padding: 0" class="menu-item" target="_blank" href="https://www.new.de/impressum">
      <v-list-item-content class="bottom-menu item-text">
        <div class="d-flex align-center">
          <v-icon left>$menuImprint</v-icon>
          Impressum & Datenschutz
        </div>
      </v-list-item-content>
    </v-list-item>
    <v-list-item style="padding: 0" class="menu-item" @click="onLogout">
      <v-list-item-content class="bottom-menu item-text">
        <div class="d-flex align-center">
          <v-icon left>$menuLogout</v-icon>
          Logout
        </div>
      </v-list-item-content>
    </v-list-item>
  </v-list>
</div>
</template>

<style scoped>
.menu-subtitle {
  font-weight: bold;
  font-size: 10px;
  letter-spacing: 0.5px;
  color: #FFFFFF;
  text-transform: uppercase;
}

.menu-item {
  cursor: pointer;
}

.menu-item:hover {
  text-decoration: none !important;
}

::v-deep .service-area-item.v-list-item--active {
  background-color: transparent !important;
}

::v-deep .service-area-item.v-list-item--active::before {
  opacity: 0 !important;
}

.item-text {
  font-size: 16px;
  font-family: "GeometriaGeometria", sans-serif;
  padding-right: 0 !important;
}

.item-text-active {
  font-family:  "GeometriaMedium", sans-serif;
  font-weight: 700;
  color: #FFFFFF;
}

.item-text-inactive {
  color: #EED9E8;
}

.menu-icon-active {
  color: #FFFFFF !important;
}

.menu-icon-inactive {
  color: #EED9E8 !important;
}

.logo-white {
  filter: brightness(0) invert(1);
}

.menu-item-row {
  width: 100%;
}

.menu-chevron {
  display: inline-block;
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 11px solid #fff;
  margin-right: -1px;
}

.search-text-field {
  margin-top: 51px !important;
}

::v-deep .search-text-field .v-input__slot {
  background-color: #810f51 !important;
}

::v-deep .search-text-field input {
  color: white !important;
}

::v-deep .search-text-field input::placeholder {
  color: white !important;
  opacity: 1;
}

.bottom-menu {
  color: #EED9E8;
}
</style>
